import React, { useEffect, useRef, useState } from 'react';
import { Loader2, AlertCircle, Trash2, FolderMinus } from 'lucide-react';
import { useDeleteRepositoryContract } from '../../../hooks/queries';
import { usePermissions } from '../../../hooks/usePermissions';
import type { RepositoryContract } from '../../../services/api/contractService';

interface DeleteRepositoryContractDialogProps {
  contract: RepositoryContract | null;
  onClose: () => void;
  /** Called after the contract is deleted or removed. */
  onDeleted?: () => void;
}

type Mode = 'remove' | 'delete';

/**
 * Confirm taking a contract out of the repository. Two choices, because a
 * contract here may also carry a review: remove only its signed documents
 * (the contract stays under Contracts), or delete the contract entirely.
 */
export const DeleteRepositoryContractDialog: React.FC<DeleteRepositoryContractDialogProps> = ({
  contract,
  onClose,
  onDeleted
}) => {
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission('contracts.delete');
  const canRemove = hasPermission('contracts.files.delete');
  const [mode, setMode] = useState<Mode>('delete');
  const mutation = useDeleteRepositoryContract();
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!contract) return;
    setMode(canDelete ? 'delete' : 'remove');
    mutation.reset();
    // Start on the harmless button.
    cancelRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract]);

  if (!contract) return null;

  const close = () => !mutation.isPending && onClose();
  const confirm = () =>
    mutation.mutate(
      { contractId: contract._id, mode },
      {
        onSuccess: () => {
          onDeleted?.();
          onClose();
        }
      }
    );

  const options: { key: Mode; allowed: boolean; icon: React.ElementType; title: string; detail: string }[] = [
    {
      key: 'delete',
      allowed: canDelete,
      icon: Trash2,
      title: 'Delete the contract',
      detail: 'Removes it everywhere, with its signed copy, drafts and review.'
    },
    {
      key: 'remove',
      allowed: canRemove,
      icon: FolderMinus,
      title: 'Only remove it from the repository',
      detail: 'Takes away the signed copy. The contract and its review stay under Contracts.'
    }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onKeyDown={(e) => e.key === 'Escape' && close()}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={close} aria-hidden />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-contract-title"
        aria-describedby="delete-contract-desc"
        className="relative w-full max-w-md bg-white dark:bg-dark-surface rounded-2xl shadow-xl border border-gray-100 dark:border-dark-border"
      >
        <div className="px-6 pt-5 pb-4 space-y-4">
          <div>
            <h2 id="delete-contract-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              Delete {contract.contractTitle}?
            </h2>
            <p id="delete-contract-desc" className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {contract.supplier?.companyName ? `${contract.supplier.companyName}. ` : ''}This can't be undone from the app.
            </p>
          </div>

          <fieldset className="space-y-2">
            <legend className="sr-only">What to delete</legend>
            {options
              .filter((o) => o.allowed)
              .map((o) => {
                const Icon = o.icon;
                const selected = mode === o.key;
                return (
                  <label
                    key={o.key}
                    className={`flex gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selected
                        ? o.key === 'delete'
                          ? 'border-rose-400 bg-rose-50/60 dark:border-rose-600 dark:bg-rose-900/20'
                          : 'border-primary-400 bg-primary-50/60 dark:border-accent-500 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-secondary'
                    }`}
                  >
                    <input
                      type="radio"
                      name="delete-mode"
                      value={o.key}
                      checked={selected}
                      onChange={() => setMode(o.key)}
                      className="mt-1"
                    />
                    <span>
                      <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white">
                        <Icon className="w-4 h-4" aria-hidden />
                        {o.title}
                      </span>
                      <span className="block mt-0.5 text-xs text-gray-600 dark:text-gray-400">{o.detail}</span>
                    </span>
                  </label>
                );
              })}
          </fieldset>

          {mutation.isError && (
            <div role="alert" className="flex items-center gap-2 p-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800">
              <AlertCircle className="w-4 h-4 flex-shrink-0" aria-hidden />
              {(mutation.error as Error)?.message || "Couldn't delete it. Try again."}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 dark:border-dark-border">
          <button
            ref={cancelRef}
            type="button"
            onClick={close}
            disabled={mutation.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-secondary transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={mutation.isPending}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 ${
              mode === 'delete' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600'
            }`}
          >
            {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" aria-hidden />}
            {mode === 'delete' ? 'Delete contract' : 'Remove from repository'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteRepositoryContractDialog;
